import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  X,
  Loader2,
  Sparkles,
  Code,
  Server,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { useCreateProject } from '@/hooks/useProject';
import { projectsApi } from '@/api/projects';
import type { CreateProjectRequest } from '@/types';

// Debounce hook for slug validation
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// Generate slug from text
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 50)
    .replace(/^-+|-+$/g, '');
}

export function NewProjectPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();

  // Form state
  const [prompt, setPrompt] = useState('');
  const [slug, setSlug] = useState('');
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  // Validation state
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [error, setError] = useState('');

  // Debounced slug for API check
  const debouncedSlug = useDebounce(slug, 500);

  // Auto-generate slug from prompt
  useEffect(() => {
    if (autoGenerateSlug && prompt) {
      const words = prompt.split(' ').slice(0, 4).join(' ');
      setSlug(generateSlug(words));
    }
  }, [prompt, autoGenerateSlug]);

  // Check slug availability
  useEffect(() => {
    if (!debouncedSlug || debouncedSlug.length < 3) {
      setSlugStatus('idle');
      return;
    }

    const checkSlug = async () => {
      setSlugStatus('checking');
      try {
        const result = await projectsApi.checkSlugAvailability(debouncedSlug);
        setSlugStatus(result.available ? 'available' : 'taken');
      } catch {
        setSlugStatus('idle');
      }
    };

    checkSlug();
  }, [debouncedSlug]);

  const handleSlugChange = (value: string) => {
    setAutoGenerateSlug(false);
    setSlug(generateSlug(value));
  };

  const handleCreate = async () => {
    if (!prompt.trim()) {
      setError('Please describe what you want to build');
      return;
    }
    if (!slug || slug.length < 3) {
      setError('Project URL must be at least 3 characters');
      return;
    }
    if (slugStatus === 'taken') {
      setError('This URL is already taken');
      return;
    }

    setError('');

    // Stack is FIXED - AI will determine what components are needed
    const projectData: CreateProjectRequest = {
      name: prompt.split(' ').slice(0, 5).join(' ') || 'New Project',
      slug,
      description: prompt,
      stackType: 'Default', // AI decides based on prompt
      hasDatabase: true, // AI decides if it's actually needed
      visibility: 'Private',
      initialPrompt: prompt,
    };

    try {
      const project = await createProject.mutateAsync(projectData);
      console.log('[NewProjectPage] Project created', {
        projectId: project.id,
        projectSlug: project.slug,
        initialConversationId: project.initialConversationId,
      });
      navigate(`/projects/${project.slug}/chat`, {
        state: {
          initialConversationId: project.initialConversationId,
          initialPrompt: prompt,
        },
      });
    } catch {
      // Error handled by mutation
    }
  };

  const SlugStatusIcon = () => {
    switch (slugStatus) {
      case 'checking':
        return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
      case 'available':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'taken':
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-900 dark:to-dark-800 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spark-400 to-spark-600 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              What do you want to build?
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Describe your idea and Spark AI will bring it to life
            </p>
          </div>

          {/* Main Form */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 p-6">
            {/* Prompt Input */}
            <div className="mb-6">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., A task management app with user authentication, drag-and-drop boards, and real-time collaboration..."
                rows={4}
                autoFocus
                className={cn(
                  'w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-700',
                  'bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-white text-lg',
                  'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                  'resize-none'
                )}
              />
            </div>

            {/* Project URL */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project URL
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400 text-sm mr-1">
                      https://
                    </span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="my-awesome-app"
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg border',
                        slugStatus === 'taken'
                          ? 'border-red-300 dark:border-red-700'
                          : 'border-gray-200 dark:border-dark-700',
                        'bg-white dark:bg-dark-800 text-gray-900 dark:text-white',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500'
                      )}
                    />
                    <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">
                      .on.applantus.com
                    </span>
                  </div>
                  <div className="absolute right-24 top-1/2 -translate-y-1/2">
                    <SlugStatusIcon />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="autoSlug"
                  checked={autoGenerateSlug}
                  onChange={(e) => setAutoGenerateSlug(e.target.checked)}
                  className="rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="autoSlug" className="text-sm text-gray-600 dark:text-gray-400">
                  Auto-generate from description
                </label>
              </div>
              {slugStatus === 'taken' && (
                <p className="text-sm text-red-500 mt-1">This URL is already taken</p>
              )}
              {slugStatus === 'available' && slug.length >= 3 && (
                <p className="text-sm text-green-500 mt-1">URL is available!</p>
              )}
            </div>

            {/* Tech Stack Info */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Built with modern tech stack
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Code className="w-4 h-4 text-primary-500" />
                  <span>React + TypeScript</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Server className="w-4 h-4 text-green-500" />
                  <span>Node.js + Express</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Database className="w-4 h-4 text-blue-500" />
                  <span>PostgreSQL</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                AI automatically determines if your app needs a backend API or database based on your requirements.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Create Button */}
            <Button
              onClick={handleCreate}
              isLoading={createProject.isPending}
              disabled={!prompt.trim() || slugStatus === 'taken' || slugStatus === 'checking'}
              className="w-full py-3 text-lg"
              leftIcon={<Sparkles className="w-5 h-5" />}
            >
              Create Project & Start Building
            </Button>

            {/* Info Text */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
              You'll be taken to the AI chat to refine your idea and generate code
            </p>
          </div>

          {/* Example Prompts */}
          <div className="mt-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-3">
              Need inspiration? Try one of these:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                'E-commerce store with cart',
                'Blog with comments',
                'Real-time chat app',
                'Portfolio website',
                'Todo app with categories',
              ].map((example) => (
                <button
                  key={example}
                  onClick={() => setPrompt(example)}
                  className="px-3 py-1.5 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-full text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-600 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
